import { message } from 'antd';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Steps } from '../api-calls';
import { t, useLanguage } from '../helpers';

const storeStepsIntoStorage = (steps) => {
  localStorage.setItem('steps', JSON.stringify(steps));
};

const compareCheckListItems = (stepFromLocal = [], step) => {
  const updateChecklist = (existingList, newList) => {
    return newList.map((newItem, i) => {
      const existing = existingList?.[i];

      if (existing) {
        return {
          ...newItem,
          isChecked: existing.isChecked,
        };
      }
      return newItem;
    });
  };

  const updateSectionsWithItems = (existingSections, newSections) => {
    return newSections.map((newSection, i) => {
      const existingSection = existingSections?.[i];

      if (existingSection) {
        const updatedItems =
          newSection.items && existingSection.items
            ? newSection.items.map((newItem, j) => {
                const existingItem = existingSection.items?.[j];

                if (existingItem) {
                  return {
                    ...newItem,
                    isChecked: existingItem.isChecked,
                  };
                }
                return newItem;
              })
            : newSection.items;

        return {
          ...newSection,
          isChecked: existingSection.isChecked,
          items: updatedItems,
        };
      }
      return newSection;
    });
  };

  const updatedThingsYouWillNeed = updateChecklist(
    stepFromLocal.thingsYouWillNeed,
    step.thingsYouWillNeed
  );

  const updatedThingsThatMightBeUseful = updateChecklist(
    stepFromLocal.thingsThatMightBeUseful,
    step.thingsThatMightBeUseful
  );
  const updatedWhatHappensInThisStage = updateChecklist(
    stepFromLocal.whatHappensInThisStage,
    step.whatHappensInThisStage
  );
  const updatedWhatWillHappenNext = updateChecklist(
    stepFromLocal.whatWillHappenNext,
    step.whatWillHappenNext
  );

  const updatedSections = updateSectionsWithItems(
    stepFromLocal.sections,
    step.sections
  );

  return {
    updatedThingsYouWillNeed,
    updatedThingsThatMightBeUseful,
    updatedWhatHappensInThisStage,
    updatedWhatWillHappenNext,
    updatedSections,
  };
};

// compare two objects and add or remove properties based on the first object
const updateStepsInStorage = (stepsFromLocal, newSteps) => {
  const updatedSteps = newSteps.map((newStep) => {
    const existingStep = stepsFromLocal.find((step) => newStep.id === step.id);

    if (existingStep) {
      const {
        updatedThingsYouWillNeed,
        updatedThingsThatMightBeUseful,
        updatedWhatHappensInThisStage,
        updatedWhatWillHappenNext,
        updatedSections,
      } = compareCheckListItems(existingStep, newStep);

      return {
        ...newStep,
        thingsYouWillNeed: updatedThingsYouWillNeed,
        thingsThatMightBeUseful: updatedThingsThatMightBeUseful,
        whatHappensInThisStage: updatedWhatHappensInThisStage,
        whatWillHappenNext: updatedWhatWillHappenNext,
        sections: updatedSections,
        isCompleted: existingStep.isCompleted,
        isInProgress: existingStep.isInProgress,
      };
    }
    return newStep;
  });
  storeStepsIntoStorage(updatedSteps);
  return updatedSteps;
};

const getStepsFromStorage = () => {
  const steps = JSON.parse(localStorage.getItem('steps'));
  return steps || [];
};

const StepsContext = createContext({
  steps: [],
  checkUncheckItem: (stepName, itemKey) => {},
});

const formateStepsObj = (stepsArr) => {
  return stepsArr.reduce((acc, curr) => {
    const { stage } = curr;
    if (!acc[stage]) {
      acc[stage] = [];
    }
    acc[stage].push(curr);
    return acc;
  }, {});
};

const StepsProvider = ({ children, ...props }) => {
  const { i18n } = useTranslation();
  const { lng } = useLanguage();
  const [steps, setSteps] = useState(getStepsFromStorage);
  const [stepsObj, setStepsObj] = useState({
    beforeClaiming: [],
    claiming: [],
    afterClaiming: [],
  });
  const [justCompletedId, setJustCompletedId] = useState('');
  const [loadingSteps, setLoadingSteps] = useState(false);
  const [stepsError, setStepsError] = useState('');
  const location = useLocation();

  const adminPages = location?.pathname?.includes('/admin/');

  const fetchData = useCallback(
    async (mounted, stage) => {
      setLoadingSteps(true);
      const { data: newSteps, error } = await Steps.getStepsContent({
        lng: adminPages ? 'en' : lng,
        stage,
      });
      if (mounted) {
        let updatedSteps = [];
        if (error) {
          setStepsError(error.message);
          message.error(t(`generalError`, lng), 2);
          // To update stepObj state
          updatedSteps = getStepsFromStorage();
        } else {
          const stepsFromLocal = getStepsFromStorage();
          updatedSteps = updateStepsInStorage(stepsFromLocal, newSteps);
          setSteps(updatedSteps);
        }

        const _stepsObj = formateStepsObj(updatedSteps);
        setStepsObj(_stepsObj);
        setLoadingSteps(false);
      }
    },
    [adminPages, lng]
  );

  useEffect(() => {
    const updatedSteps = steps.map((step) => {
      if (step.id === justCompletedId) {
        return { ...step, isCompleted: true };
      }
      return step;
    });
    const _stepsObj = formateStepsObj(updatedSteps);
    i18n.addResourceBundle(lng, 'stepsObj', {
      stepsObj: _stepsObj,
    });
    setStepsObj(_stepsObj);
  }, [justCompletedId, steps, i18n, lng]);

  const checkUncheckItem = (stepId, index, key, itemIndex = null) => {
    setSteps((prevSteps) => {
      const newSteps = prevSteps.map((step) => {
        if (step.id === stepId) {
          const newStep = { ...step };

          // const processSection = (sectionKey) => {
          //   if (!step[sectionKey]?.length)
          //     return { items: [], uncheckedCount: 0 };
          // };

          let uncheckedStepsThingsYouWillNeed = step?.thingsYouWillNeed?.length;
          const newCheckListItemsThingsYouWillNeed = step.thingsYouWillNeed.map(
            (item, checklistIndex) => {
              const newItem = { ...item };

              if (checklistIndex === index && key === 'thingsYouWillNeed') {
                newItem.isChecked = !item.isChecked;
              }

              if (newItem.isChecked) {
                uncheckedStepsThingsYouWillNeed--;
              }

              return newItem;
            }
          );

          newStep.thingsYouWillNeed = newCheckListItemsThingsYouWillNeed;

          let uncheckedStepsThingsThatMightBeUseful =
            step?.thingsThatMightBeUseful?.length;
          const newCheckListItemsThingsThatMightBeUseful =
            step.thingsThatMightBeUseful.map((item, checklistIndex) => {
              const newItem = { ...item };

              if (
                checklistIndex === index &&
                key === 'thingsThatMightBeUseful'
              ) {
                newItem.isChecked = !item.isChecked;
              }

              if (newItem.isChecked) {
                uncheckedStepsThingsThatMightBeUseful--;
              }

              return newItem;
            });

          newStep.thingsThatMightBeUseful =
            newCheckListItemsThingsThatMightBeUseful;

          let uncheckedStepsWhatHappensInThisStage =
            step?.whatHappensInThisStage?.length;
          const newCheckListItemsWhatHappensInThisStage =
            step.whatHappensInThisStage.map((item, checklistIndex) => {
              const newItem = { ...item };

              if (
                checklistIndex === index &&
                key === 'whatHappensInThisStage'
              ) {
                newItem.isChecked = !item.isChecked;
              }

              if (newItem.isChecked) {
                uncheckedStepsWhatHappensInThisStage--;
              }

              return newItem;
            });

          newStep.whatHappensInThisStage =
            newCheckListItemsWhatHappensInThisStage;

          let uncheckedStepsWhatWillHappenNext =
            step?.whatWillHappenNext?.length;
          const newCheckListItemswhatWillHappenNext =
            step.whatWillHappenNext.map((item, checklistIndex) => {
              const newItem = { ...item };

              if (checklistIndex === index && key === 'whatWillHappenNext') {
                newItem.isChecked = !item.isChecked;
              }

              if (newItem.isChecked) {
                uncheckedStepsWhatWillHappenNext--;
              }

              return newItem;
            });

          newStep.whatWillHappenNext = newCheckListItemswhatWillHappenNext;

          let uncheckedSectionItems = 0;
          let totalSectionItems = 0;
          let newSections = [];

          if (step.sections && step.sections.length > 0) {
            newSections = step.sections.map((section, sectionIndex) => {
              const newSection = { ...section };

              if (
                sectionIndex === index &&
                key === 'sections' &&
                itemIndex === null
              ) {
                newSection.isChecked = !section.isChecked;
              }

              let sectionItemsCheckedCount = 0;
              if (newSection.items && newSection.items.length > 0) {
                totalSectionItems += newSection.items.length;

                newSection.items = newSection.items.map((item, idx) => {
                  const newItem = { ...item };

                  if (
                    sectionIndex === index &&
                    key === 'sections' &&
                    itemIndex === idx
                  ) {
                    newItem.isChecked = !item.isChecked;
                  }

                  if (newItem.isChecked) {
                    sectionItemsCheckedCount++;
                  }

                  return newItem;
                });

                if (sectionItemsCheckedCount === newSection.items.length) {
                  newSection.isChecked = true;
                }

                uncheckedSectionItems +=
                  newSection.items.length - sectionItemsCheckedCount;
              } else if (!newSection.isChecked) {
                uncheckedSectionItems++;
                totalSectionItems++;
              }

              return newSection;
            });
          }

          newStep.sections = newSections;

          newStep.isCompleted =
            newStep.isCompleted ||
            (uncheckedStepsThingsYouWillNeed === 0 &&
              uncheckedStepsThingsThatMightBeUseful === 0 &&
              uncheckedStepsWhatHappensInThisStage === 0 &&
              uncheckedStepsWhatWillHappenNext === 0 &&
              uncheckedSectionItems === 0);

          newStep.isInProgress =
            newStep.isInProgress ||
            (!!step?.thingsYouWillNeed?.length &&
              uncheckedStepsThingsYouWillNeed <
                step?.thingsYouWillNeed?.length) ||
            (!!step?.thingsThatMightBeUseful?.length &&
              uncheckedStepsThingsThatMightBeUseful <
                step?.thingsThatMightBeUseful?.length) ||
            (!!step?.whatHappensInThisStage?.length &&
              uncheckedStepsWhatHappensInThisStage <
                step?.whatHappensInThisStage?.length) ||
            (!!step?.whatWillHappenNext?.length &&
              uncheckedStepsWhatWillHappenNext <
                step?.whatWillHappenNext?.length) ||
            (totalSectionItems > 0 &&
              uncheckedSectionItems < totalSectionItems);

          return newStep;
        }
        return { ...step };
      });

      storeStepsIntoStorage(newSteps);
      return newSteps;
    });
  };

  const markAsComplete = (stepId) => {
    setSteps((prevSteps) => {
      let lastCompletedStepStageId;
      const newSteps = prevSteps.map((step) => {
        if (step.id === stepId) {
          const thingsYouWillNeed = step.thingsYouWillNeed.map((item) => ({
            ...item,
            isChecked: true,
          }));

          const thingsThatMightBeUseful = step.thingsThatMightBeUseful.map(
            (item) => ({
              ...item,
              isChecked: true,
            })
          );

          const whatHappensInThisStage = step.whatHappensInThisStage.map(
            (item) => ({
              ...item,
              isChecked: true,
            })
          );

          const whatWillHappenNext = step.whatWillHappenNext.map((item) => ({
            ...item,
            isChecked: true,
          }));

          const sections = (step.sections || []).map((section) => {
            const updatedItems = section.items
              ? section.items.map((item) => ({
                  ...item,
                  isChecked: true,
                }))
              : [];

            return {
              ...section,
              isChecked: true,
              items: updatedItems,
            };
          });

          lastCompletedStepStageId = step.stageId;

          const completedStep = {
            ...step,
            thingsYouWillNeed,
            thingsThatMightBeUseful,
            whatHappensInThisStage,
            whatWillHappenNext,
            sections,
            isInProgress: false,
            isCompleted: true,
          };

          storeStepsIntoStorage(completedStep);

          return completedStep;
        }

        // Mark next step after the completed one as in progress (Required in #40)
        if (
          step.stageId === lastCompletedStepStageId &&
          !step.isCompleted &&
          !step.isInProgress
        ) {
          // Do this for only 1 next step in the current stage
          lastCompletedStepStageId = null;
          return {
            ...step,
            isInProgress: true,
          };
        }

        return { ...step };
      });

      storeStepsIntoStorage(newSteps);
      setJustCompletedId(stepId);
      return newSteps;
    });
  };

  const markAsInProgress = useCallback(
    (stepId) => {
      const _steps = steps.map((step) => {
        if (step.id === stepId && !step.isInProgress && !step.isCompleted) {
          return {
            ...step,
            isInProgress: true,
          };
        }
        return step;
      });

      setSteps(_steps);
      storeStepsIntoStorage(_steps);
    },
    [steps]
  );

  const value = {
    steps,
    stepsObj,
    checkUncheckItem,
    justCompletedId,
    setJustCompletedId,
    stepsError,
    loadingSteps,
    markAsComplete,
    markAsInProgress,
    fetchSteps: fetchData,
  };
  return (
    <StepsContext.Provider value={value} {...props}>
      {children}
    </StepsContext.Provider>
  );
};

export const useSteps = () => {
  const value = useContext(StepsContext);
  return value;
};

export default StepsProvider;
